import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import TaskLinearTable from "./TaskLinearTable";
import { useTheme } from '@mui/material/styles';

import {
  TextField,
  Button,
  Snackbar,
  Alert,
  Box,
  Backdrop,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import TaskComponent from "./TaskCalendar";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTaskAction,
  deleteTaskActionById,
  searchTaskCalendarAction,
  searchCommonTaskAction,
  getClientCustomerId,
  getDueTaskAction,
  getTypeOfTaskOpenListAction,
  getCustomerFromPolicyAction,
} from "../../../redux/actions/taskActions";
import TaskPopover from "./TaskPopover";
import Loader from "../../loader/loader";
import { useLocation } from "react-router-dom";

const SnackbarComponent = ({ open, message, severity, onClose }) => (
  <Snackbar
    open={open}
    autoHideDuration={3000}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert onClose={onClose} severity={severity} sx={{ width: "100%" }}>
      {message}
    </Alert>
  </Snackbar>
);

const CalendarComponent = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDateTasks, setSelectedDateTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isEditTask, setIsEditTask] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOopOpen, setSnackbarOopOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [deleteSnackbarOpen, setDeleteSnackbarOpen] = useState(false);
  const [getTaskData, setGetTaskData] = useState([]);
  const location = useLocation();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverData, setPopoverData] = useState(null);

  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState([]);
  
  // FIX 1: Separate task component state to prevent flickering
  const [taskComponentOpen, setTaskComponentOpen] = useState(false);
  const [taskComponentMounted, setTaskComponentMounted] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [selectedEventData, setSelectedEventData] = useState(null);

  const taskData = useSelector((state) => state.task.allTask);
  const taskList = taskData.content || [];
  const searchResults = useSelector(
    (state) => state.task.searchTaskCalendar || []
  );
  const commonTasks = useSelector((state) => state.task.searchCommonTask || []);
  const selectedTask = useSelector(
    (state) => state.notificationPanel.selectedTask
  );
  const clientCustomer = useSelector((state) => state.task.clientCustomer);
  const dueTask = useSelector((state) => state.task.dueTask);
  const [selectedTaskType, setSelectedTaskType] = useState(null);

  const [dateRange, setDateRange] = useState({ fromDate: "", toDate: "" });
  const [searchSelectedButton, setSearchSelectedButton] = useState(null);
  const [activeButton, setActiveButton] = useState(null);

  // FIX 2: Debounced state updates to prevent rapid re-renders
  const debounceTimeoutRef = useRef(null);
  
  const handleButtonClick = (label, onClick) => {
    setActiveButton(label);
    onClick();
  };

  // FIX 3: Stable loading handler with proper cleanup
  const debouncedSetLoading = useCallback((value) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    if (value === true) {
      setIsLoading(true);
    } else {
      debounceTimeoutRef.current = setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    dispatch(getTypeOfTaskOpenListAction());
  }, [dispatch]);

  const calendarRef = useRef(null);

  const handleDatesSet = useCallback((arg) => {
    const viewType = arg.view.type;
    let fromDate, toDate;

    if (viewType === "dayGridMonth") {
      const selectedDate = new Date(arg.view.currentStart);
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      fromDate = new Date(selectedYear, selectedMonth, 1);
      toDate = new Date(selectedYear, selectedMonth + 1, 0);
    } else if (viewType === "timeGridWeek") {
      fromDate = new Date(arg.start);
      toDate = new Date(arg.end);
      toDate.setDate(toDate.getDate() - 1);
    } else if (viewType === "timeGridDay") {
      fromDate = new Date(arg.start);
      toDate = new Date(arg.end);
      toDate.setDate(toDate.getDate() - 1);
    }

    const formatDate = (date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`;

    const formattedFrom = formatDate(fromDate);
    const formattedTo = formatDate(toDate);

    setDateRange({ fromDate: formattedFrom, toDate: formattedTo });
    dispatch(getAllTaskAction(0, 200, formattedFrom, formattedTo));
  }, [dispatch]);

  const handleChangePage = useCallback((_e, newPage) => setPage(newPage), []);
  
  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);

  const paginatedTasks = useMemo(() => 
    selectedDateTasks.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    ), [selectedDateTasks, page, rowsPerPage]
  );

  // FIX 4: Memoize edit handler to prevent recreation on each render
  const handleEdit = useCallback((task) => {
    if (!task) return;

    try {
      // Set editing state first
      setIsEditTask(true);
      setEditingEventId(task.id);
      setGetTaskData(task);

      let firstName = "";
      let lastName = "";
      if (task.customerName) {
        const nameParts = task.customerName.split(" ");
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(" ");
      }

      const selectedEventData = {
        id: task.id,
        clientCustomerId: task.clientCustomerId,
        policyId: task.policyId,
        dueDate: task.dueDate,
        taskType: task.taskType,
        assignedToName: task.assignedToName,
        assignedTo: task.assignedTo,
        status: task.status,
        taskPriority: task.priority,
        taskDescription: task.taskDescription,
        policyNumber: task.policyId
          ? { id: task.policyId }
          : null,
        customer: task.clientCustomerId
          ? {
              id: task.clientCustomerId,
              customerNumber: task.customerNumber || "",
              firstName: firstName || "",
              lastName: lastName || "",
            }
          : null,
      };

      setSelectedEventData(selectedEventData);

      // FIX 5: Open task component after state is set
      setTimeout(() => {
        setTaskComponentOpen(true);
        setTaskComponentMounted(true);
      }, 50);

      // Load additional data
      if (task.clientCustomerId) {
        dispatch(getClientCustomerId(task.clientCustomerId));
      }

      const policyIdExists = task.policyId && 
                            task.policyId !== null && 
                            task.policyId !== undefined && 
                            task.policyId !== '' &&
                            task.policyId !== 'null' &&
                            task.policyId !== 'undefined';

      if (policyIdExists) {
        dispatch(getCustomerFromPolicyAction(task.policyId))
          .then((response) => {
            if (response?.payload) {
              const policyData = response.payload;
              setSelectedEventData(prev => ({
                ...prev,
                policyNumber: {
                  id: task.policyId,
                  policyNumber: policyData.policyNumber || task.policyId
                },
                customer: {
                  id: policyData.clientCustomerId || task.clientCustomerId,
                  firstName: policyData.firstName || firstName,
                  lastName: policyData.lastName || lastName,
                  customerNumber: policyData.customerNumber || task.customerNumber || ""
                }
              }));
            }
          })
          .catch(() => {});
      }
    } catch (error) {
      console.error("Error in handleEdit:", error);
    }
  }, [dispatch]);

  // FIX 6: Stable event mapping with proper dependencies
  const mappedEvents = useMemo(() => {
    let source = [];

    if (searchSelectedButton === "search") {
      source = searchResults?.content?.length > 0 ? searchResults.content : [];
    } else if (selectedTaskType) {
      source = (
        commonTasks?.addTaskList?.content?.length > 0
          ? commonTasks.addTaskList.content
          : taskList
      ).filter((task) => task.taskType === selectedTaskType);
    } else if (activeButton === "Past Due Date") {
      source = dueTask?.content || [];
    } else {
      source = taskList;
    }

    return source.map((task) => ({
      id: task.id.toString(),
      title: `${task.status} - ${task.taskType || "N/A"}`,
      start: task.dueDate,
      end: task.dueDate,
      classNames:
        selectedTask?.id === task.id ? ["custom-highlighted-task"] : [],
      extendedProps: {
        description: task.taskDescription,
        priority: task.priority,
        assignedToName: task.assignedToName,
      },
    }));
  }, [
    taskList,
    commonTasks,
    searchResults,
    dueTask,
    searchSelectedButton,
    selectedTask,
    selectedTaskType,
    activeButton,
  ]);

  // FIX 7: Optimize effect dependencies and reduce unnecessary updates
  useEffect(() => {
    const mappedEvents = dueTask?.content?.map((task) => ({
      id: task.id.toString(),
      title: `${task.status} - ${task.taskType || "N/A"}`,
      start: task.dueDate,
      end: task.dueDate,
      extendedProps: {
        description: task.taskDescription,
        priority: task.priority,
        assignedToName: task.assignedToName,
      },
    }));
    if (mappedEvents) {
      setEvents(mappedEvents);
    }
  }, [dueTask?.content]); // More specific dependency

  const fetchDueTasks = useCallback(() => {
    const today = new Date();
    const fromDate = new Date(today.getFullYear(), today.getMonth(), 2);
    const toDate = new Date(today);
    toDate.setDate(toDate.getDate() - 1);

    const formattedFromDate = fromDate.toISOString();
    const formattedToDate = toDate.toISOString();

    const status = "Open";
    const page = 0;
    const size = 100;

    dispatch(
      getDueTaskAction(page, size, formattedFromDate, formattedToDate, status)
    );
  }, [dispatch]);

  useEffect(() => {
    if (clientCustomer) {
      setSelectedEventData((prev) => prev ? ({
        ...prev,
        firstName: clientCustomer.firstName,
        lastName: clientCustomer.lastName,
      }) : null);
    }
  }, [clientCustomer]);

  const getShortDescription = useCallback((text) => {
    const words = text?.split(" ");
    if (words?.length > 3) return words.slice(0, 3).join(" ") + "...";
    return text;
  }, []);

  useEffect(() => {
    return () => {
      setPopoverOpen(false);
      dispatch({ type: "SET_SELECTED_TASK", payload: null });
    };
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (selectedTask) {
      const mappedEvent = {
        id: selectedTask.id.toString(),
        title: `${selectedTask.status} - ${selectedTask.taskType || "N/A"}`,
        start: selectedTask.dueDate,
        end: selectedTask.dueDate,
        extendedProps: {
          description: selectedTask.taskDescription,
          priority: selectedTask.priority,
          assignedToName: selectedTask.assignedToName,
        },
      };

      setEvents([mappedEvent]);
      if (calendarRef.current?.getApi) {
        calendarRef.current.getApi().gotoDate(selectedTask.dueDate);
      }
      setPopoverData({
        id: mappedEvent.id,
        status: selectedTask.status,
        taskType: selectedTask.taskType || "",
        taskDescription: selectedTask.taskDescription || "",
        priority: selectedTask.priority || "",
        assignedToName: selectedTask.assignedToName || "",
        dueDate: dayjs(selectedTask.dueDate).format("MM-DD-YYYY"),
      });
      setPopoverOpen(true);
    }
  }, [selectedTask]);

  useEffect(() => {
    const tasks = commonTasks?.addTaskList?.content || [];
    if (tasks.length > 0) {
      const mappedEvents = tasks.map((task) => ({
        id: task.id.toString(),
        title: `${task.status} - ${task.taskType || "N/A"}`,
        start: task.dueDate,
        end: task.dueDate,
        extendedProps: {
          description: task.taskDescription,
          priority: task.priority,
          assignedToName: task.assignedToName,
        },
      }));
      setEvents(mappedEvents);
    } else {
      setEvents([]);
    }
  }, [commonTasks?.addTaskList?.content]); // More specific dependency

  useEffect(() => {
    const tasks = searchResults?.content || [];
    if (tasks.length > 0) {
      const mappedEvents = tasks.map((task) => ({
        id: task.id.toString(),
        title: `${task.status} - ${task.taskType || "N/A"}`,
        start: task.dueDate,
        end: task.dueDate,
        extendedProps: {
          description: task.taskDescription,
          priority: task.priority,
          assignedToName: task.assignedToName,
        },
      }));
      setEvents(mappedEvents);
    }
  }, [searchResults?.content]); // More specific dependency

  // Style injection - moved to useEffect to prevent re-creation
  useEffect(() => {
    if (!document.getElementById("custom-task-style")) {
      const style = document.createElement("style");
      style.id = "custom-task-style";
      style.innerHTML = `
        .fc-event.custom-highlighted-task {
          background-color: green !important;
          color: white !important;
          border: none !important;
        }
        .fc-event.custom-highlighted-task .fc-event-title,
        .fc-event.custom-highlighted-task .fc-event-time {
          color: white !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []); // Empty dependency array - only run once

  useEffect(() => {
    const applyCustomButtonStyles = () => {
      const buttons = document.querySelectorAll(
        ".fc-button, .fc-prev-button, .fc-next-button, .fc-today-button, .fc-dayGridMonth-button, .fc-timeGridWeek-button, .fc-timeGridDay-button"
      );
      buttons.forEach((button) => {
        button.style.backgroundColor = "white";
        button.style.color = "black";
        button.style.border = `2px solid ${theme.palette.primary.main}`;
        button.style.width = "50";
        button.style.height = "25";
        button.style.borderRadius = "4px";
        button.style.margin = "2px";
      });
    };
    applyCustomButtonStyles();
    const timeoutId = setTimeout(applyCustomButtonStyles, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  const fetchTasks = useCallback(() => {
    debouncedSetLoading(true);
    dispatch(getAllTaskAction(0, 200))
      .then(() => debouncedSetLoading(false))
      .catch(() => debouncedSetLoading(false));
  }, [dispatch, debouncedSetLoading]);

  // FIX 8: Improved task component handlers
  const handleAddNewTask = useCallback(() => {
    setEditingEventId(null);
    setSelectedEventData(null);
    setIsEditTask(false);
    setTaskComponentMounted(true);
    setTimeout(() => {
      setTaskComponentOpen(true);
    }, 50);
  }, []);

  const handleTaskComponentClose = useCallback(() => {
    setTaskComponentOpen(false);
    setTimeout(() => {
      setTaskComponentMounted(false);
      setIsEditTask(false);
      setEditingEventId(null);
      setSelectedEventData(null);
    }, 300); // Wait for close animation
  }, []);

  const handleDateClick = useCallback((info) => {
    const clickedDate = new Date(info.date);
    const today = new Date();

    clickedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const clickedDateStr = info.dateStr;

    if (clickedDate >= today) {
      setEditingEventId(null);
      setSelectedEventData({ dueDate: dayjs(clickedDateStr) });
      setIsEditTask(false);
      setTaskComponentMounted(true);
      setTimeout(() => {
        setTaskComponentOpen(true);
      }, 50);
    } else {
      const filteredTasks = taskList.filter(
        (task) => dayjs(task.dueDate).format("YYYY-MM-DD") === clickedDateStr
      );
      setSelectedDate(clickedDateStr);
      setSelectedDateTasks(filteredTasks);
      setSnackbarOopOpen(true);
    }
  }, [taskList]);

  const handleEventClick = useCallback((info) => {
    const eventId = info.event.id;
    const eventToEdit = mappedEvents.find((e) => e.id === eventId);

    if (eventToEdit) {
      const titleParts = eventToEdit.title.split(" - ");
      const status = titleParts[0];
      const taskType = titleParts[1] || "";

      setPopoverData({
        id: eventId,
        status,
        taskType,
        taskDescription: eventToEdit.extendedProps?.description || "",
        priority: eventToEdit.extendedProps?.priority || "",
        assignedToName: eventToEdit.extendedProps?.assignedToName || "",
        dueDate: dayjs(eventToEdit.start).format("MM-DD-YYYY"),
      });

      setAnchorEl(info.jsEvent.currentTarget);
      setPopoverOpen(true);
    }
  }, [mappedEvents]);

  const handleTaskSubmission = useCallback((formValues) => {
    if (formValues) {
      dispatch(getAllTaskAction(0, 200));
      const eventTitle = `${formValues.status} - ${formValues.taskType}`;

      if (editingEventId) {
        setEvents(prev =>
          prev.map((event) =>
            event.id === editingEventId
              ? {
                  ...event,
                  title: eventTitle,
                  start: formValues.dueDate.format("MM-DD-YYYY"),
                  end: formValues.dueDate.format("MM-DD-YYYY"),
                  extendedProps: {
                    description: formValues.taskDescription,
                    priority: formValues.taskPriority,
                    assignedToName: formValues.assignedToName,
                    clientCustomerId: formValues.clientCustomerId,
                  },
                }
              : event
          )
        );
      } else {
        const newEvent = {
          id: Date.now().toString(),
          title: eventTitle,
          start: formValues.dueDate.format("MM-DD-YYYY"),
          end: formValues.dueDate.format("MM-DD-YYYY"),
          extendedProps: {
            description: formValues.taskDescription,
            priority: formValues.taskPriority,
            assignedToName: formValues.assignedToName,
          },
        };
        setEvents(prev => [...prev, newEvent]);
      }

      handleTaskComponentClose();
    }
  }, [editingEventId, dispatch, handleTaskComponentClose]);

  const handleTaskDeletion = useCallback(() => {
    if (editingEventId) {
      debouncedSetLoading(true);
      dispatch(deleteTaskActionById(editingEventId))
        .then(() => {
          fetchTasks();
          setDeleteSnackbarOpen(true);
        })
        .catch(() => debouncedSetLoading(false));

      setPopoverOpen(false);
      handleTaskComponentClose();
    }
  }, [editingEventId, dispatch, debouncedSetLoading, fetchTasks, handleTaskComponentClose]);

  const handleSearch = useCallback(() => {
    setSearchSelectedButton("search");
    debouncedSetLoading(true);

    if (!searchQuery.trim()) {
      resetAllTasks();
      debouncedSetLoading(false);
      return;
    }

    dispatch(searchTaskCalendarAction({ query: searchQuery })).finally(() => {
      debouncedSetLoading(false);
    });
  }, [searchQuery, debouncedSetLoading, dispatch]);

  const resetAllTasks = useCallback(() => {
    debouncedSetLoading(true);
    const allEvents = taskList.map((task) => ({
      id: task.id.toString(),
      title: `${task.status} - ${task.taskType || "N/A"}`,
      start: task.dueDate,
      end: task.dueDate,
      extendedProps: {
        description: task.taskDescription,
        priority: task.priority,
        assignedToName: task.assignedToName,
      },
    }));
    setEvents(allEvents);
    setTimeout(() => debouncedSetLoading(false), 300);
  }, [taskList, debouncedSetLoading]);

const handleReset = useCallback(() => {
  debouncedSetLoading(true);
  setSearchSelectedButton("reset");
  setSearchQuery("");
  setSelectedTaskType(null);
  
  const { fromDate, toDate } = dateRange;

  dispatch(getAllTaskAction(0, 200, fromDate, toDate))
    .then(() => debouncedSetLoading(false))
    .catch(() => debouncedSetLoading(false));
}, [dispatch, debouncedSetLoading, dateRange]);


  // FIX 9: Memoized CustomTaskComponent to prevent recreation
  const CustomTaskComponent = useMemo(() => {
    const isEditingTask = selectedEventData && selectedEventData.id;
    
    // Only render if component should be mounted
    if (!taskComponentMounted) return null;
    
    return (
      <TaskComponent
        taskComponentOpen={taskComponentOpen}
        isEditTask={isEditTask}
        setIsEditTask={setIsEditTask}
        setTaskComponentOpen={setTaskComponentOpen}
        open={taskComponentOpen}
        handleClose={handleTaskComponentClose}
        initialValues={selectedEventData || {}}
        onSubmit={handleTaskSubmission}
        onDelete={handleTaskDeletion}
        isEditing={isEditingTask ? selectedEventData : null}
        editId={editingEventId}
        handleSnackbar={(msg, sev) => {
          setSnackbarMessage(msg);
          setSnackbarSeverity(sev || "success");
          setSnackbarOpen(true);
        }}
        isLoading={isLoading}
        setIsLoading={debouncedSetLoading}
        getTaskData={getTaskData}
      />
    );
  }, [
    taskComponentMounted,
    taskComponentOpen,
    isEditTask,
    selectedEventData,
    editingEventId,
    isLoading,
    getTaskData,
    handleTaskComponentClose,
    handleTaskSubmission,
    handleTaskDeletion,
    debouncedSetLoading
  ]);

  const handleSearchTask = useCallback((taskType) => {
    debouncedSetLoading(true);
    let query = "";

    switch (taskType) {
      case "Renewal":
        query = "Renewal Offer Review";
        break;
      case "Pending Cancellation":
        query = "Pending Cancellation for UW";
        break;
      case "Cancellation":
        query = "Cancelled policy";
        break;
      default:
        return;
    }
    setSelectedTaskType(query);

    const formData = {
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
      taskType: query,
      page: 0,
      size: 50,
      sort: "desc",
    };

    Promise.resolve(dispatch(searchCommonTaskAction(formData))).finally(() => {
      debouncedSetLoading(false);
    });
  }, [dateRange, dispatch, debouncedSetLoading]);

  const handleDelete = useCallback((taskId) => {
    dispatch(deleteTaskActionById(taskId))
      .then(() => {
        return fetchTasks();
      })
      .then(() => {
        debouncedSetLoading(false);
      })
      .catch(() => {
        debouncedSetLoading(false);
      });
    setSelectedDateTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, [dispatch, fetchTasks, debouncedSetLoading]);

  // Memoized popover edit handler
  const handlePopoverEdit = useCallback(() => {
    setIsEditTask(true);
    setTaskComponentMounted(true);
    
    setTimeout(() => {
      setTaskComponentOpen(true);
      setSelectedEventData({
        id: popoverData.id,
        dueDate: popoverData.dueDate,
        taskType: popoverData.taskType,
        assignedToName: popoverData.assignedToName,
        status: popoverData.status,
        taskPriority: popoverData.priority,
        taskDescription: popoverData.taskDescription,
      });

      const taskToEdit = taskList.find(
        (task) => task.id.toString() === popoverData.id
      );
      if (taskToEdit) {
        setGetTaskData(taskToEdit);
        handleEdit(taskToEdit);
      }

      setEditingEventId(popoverData.id);
      setPopoverOpen(false);
    }, 50);
  }, [popoverData, taskList, handleEdit]);

  return (
    <>
      <style>
        {`
        .custom-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 999;
        }

        .modal-card {
          background: #fff;
          padding: 20px;
          border-radius: 8px;
          width: 90%;
          max-width: 900px;
          max-height: 80vh;
          overflow-y: auto;
        }

        .task-table {
          width: 100%;
          border-collapse: collapse;
        }

        .task-table th,
        .task-table td {
          border: 1px solid #eee;
          padding: 8px;
          text-align: left;
        }

        .task-table th {
          background-color: #fcd34d;
        }
      `}
      </style>
      
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: "opacity 0.3s ease-in-out",
        }}
        open={isLoading}
        transitionDuration={300}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center", 
            height: "100vh", 
          }}
        >
          <Loader />
        </Box>
      </Backdrop>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          width: "100%",
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          padding: "20px 0",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "150px auto 150px",
            gap: "20px",
            width: "100%",
            maxWidth: "1200px",
            backgroundColor: "#fff",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              width: "150px",
            }}
          >
            <Typography
            >
              Common Tasks
            </Typography>
            {[
              { label: "Renewal", onClick: () => handleSearchTask("Renewal") },
              {
                label: "Pending Cancellation",
                onClick: () => handleSearchTask("Pending Cancellation"),
              },
              {
                label: "Cancelled Policy",
                onClick: () => handleSearchTask("Cancellation"),
              },
              { label: "Reset All", onClick: () => handleReset() },
              { label: "Past Due Date", onClick: () => fetchDueTasks() },
            ].map(({ label, onClick }) => (
              <Button
                variant="outlined"
                key={label}
                onClick={() => handleButtonClick(label, onClick)}
              >
                {label}
              </Button>
            ))}
          </div>

          <div style={{ width: "100%" }}>
            <div
              style={{
                backgroundColor: "#ffffff",
                padding: "10px",
                border: `4px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                events={mappedEvents}
                dayMaxEvents={3}
                moreLinkClick={(arg) => {
                  const clickedDate = dayjs(arg.date).format("YYYY-MM-DD");

                  const filteredTasks = taskList.filter(
                    (task) =>
                      dayjs(task.dueDate).format("YYYY-MM-DD") === clickedDate
                  );

                  setSelectedDate(clickedDate);
                  setSelectedDateTasks(filteredTasks);
                  setModalOpen(true);

                  return "none";
                }}
                moreLinkContent={(arg) => {
                  return `more (+${arg.num})`;
                }}
                ref={calendarRef}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                editable={true}
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,timeGridDay",
                }}
                buttonText={{
                  today: "Today",
                  month: "Month",
                  week: "Week",
                  day: "Day",
                }}
                allDayText="All-Day"
                aspectRatio={1.8}
                height="auto"
                fixedWeekCount={false}
                datesSet={handleDatesSet}
                eventDidMount={(info) => {
                  info.el.style.backgroundColor = theme.palette.primary.main;
                  info.el.style.borderRadius = "4px";
                  info.el.style.padding = "2px 4px";
                  const titleElement = info.el.querySelector(".fc-event-title");
                  if (titleElement) titleElement.style.color = "black";
                  const allChildElements = info.el.querySelectorAll("*");
                  allChildElements.forEach((el) => (el.style.color = theme.palette.primary.contrastText ));
                }}
              />

              {modalOpen && (
                <div className="custom-modal">
                  <div className="modal-card">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      padding="10px"
                    >
                      <h3
                        style={{
                          flex: 1,
                          textAlign: "center",
                          marginLeft: "80px",
                        }}
                      >
                        Tasks on{" "}
                        {(() => {
                          const date = new Date(selectedDate);
                          const mm = String(date.getMonth() + 1).padStart(
                            2,
                            "0"
                          );
                          const dd = String(date.getDate()).padStart(2, "0");
                          const yyyy = date.getFullYear();
                          return `${mm}-${dd}-${yyyy}`;
                        })()}
                      </h3>

                      <Button
                        onClick={() => setModalOpen(false)}
                        sx={{
                          backgroundColor: "white",
                          color: "black",
                          width: 100,
                          height: 50,
                        }}
                      >
                        Close
                      </Button>
                    </Box>

                    <TaskLinearTable
                      handleDelete={handleDelete}
                      paginatedTasks={paginatedTasks}
                      getShortDescription={getShortDescription}
                      handleEdit={handleEdit}
                      selectedDateTasks={selectedDateTasks}
                      handleChangePage={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      handleChangeRowsPerPage={handleChangeRowsPerPage}
                      page={page}
                     />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              alignItems: "flex-start",
              width: "150px", 
              marginTop: "40px",
            }}
          >
            <Button
              variant="contained"
              style={{
                width: "100%",
                width: 150,
                height: 60,
                textTransform: 'none', fontSize: '15px'
              }}
              onClick={handleAddNewTask}
            >
              Add Task
            </Button>
            <TextField
              placeholder="Task Search"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: "5px",
                width: 150,
                height: 60,
              }}
            />
            <Button
              onClick={handleSearch}
              variant="outlined"
            >
              Search
            </Button>

            <Button
              onClick={handleReset}
              variant="outlined"
            >
              Reset
            </Button>
          </div>

          {/* Task Modal and Popover */}
          {CustomTaskComponent}

          <TaskPopover
            anchorEl={anchorEl}
            open={popoverOpen}
            handleClose={() => setPopoverOpen(false)}
            eventData={popoverData}
            onEdit={handlePopoverEdit}
          />
        </div>
        
        <SnackbarComponent
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />

        <Snackbar
          open={snackbarOopOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOopOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOopOpen(false)}
            severity="warning"
            sx={{ width: "100%" }}
          >
            Oops! Past dates are not allowed.
          </Alert>
        </Snackbar>
        
        <Snackbar
          open={deleteSnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setDeleteSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setDeleteSnackbarOpen(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Task Deleted Successfully
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default CalendarComponent;
